/**
 * The title block at the top of every page: icon, heading, subtitle, and the
 * actions that belong to that page (view toggle, import/export, AI, "new X").
 */
import { useNavigate } from 'react-router-dom';
import { PAGE_META } from '../constants/defaults.js';
import Icon from '../lib/icons.jsx';
import { Spinner } from './Primitives.jsx';
import { useApp } from '../store/AppContext.jsx';
import AskAI from './AskAI.jsx';

const LIST_VIEWS = ['clients', 'trials', 'payments', 'renewals'];
const CRM_VIEWS = ['sales', 'pipeline', 'deals', 'contacts', 'companies', 'activities'];
const RISK_VIEWS = ['clients', 'dashboard', 'trials', 'payments', 'renewals'];

/*
 * Every marketing screen used to fall through to the client button, so the
 * Campaigns page offered "New client" and there was no way to start a campaign
 * from the page named after them. Each one names its own primary action here.
 */
const MARKETING_ACTIONS = {
  marketing: ['campaign', 'New campaign'],
  'mkt-email': ['campaign', 'New campaign', 'email'],
  'mkt-whatsapp': ['campaign', 'New campaign', 'whatsapp'],
  'mkt-sms': ['campaign', 'New campaign', 'sms'],
  'mkt-push': ['campaign', 'New campaign', 'push'],
  'mkt-journeys': ['journey', 'New journey'],
  'mkt-contacts': ['mktContact', 'New contact'],
  'mkt-audiences': ['audience', 'New audience'],
  'mkt-templates': ['template', 'New template']
};

const TRACK_VIEWS = ['utm-dashboard', 'utm-campaigns', 'utm-links', 'utm-create', 'utm-analytics'];

/*
 * The primary action per URL Tracking screen: [label, icon, what it does,
 * which menu and verb must allow it].
 *
 * Creating a URL is a screen rather than a sheet, so that one navigates; the
 * campaign editor is a sheet like every other editor in the workspace.
 */
const TRACK_ACTIONS = {
  'utm-dashboard': ['New campaign', 'plus', { sheet: 'trackCampaign' }, ['utm-campaigns', 'create']],
  'utm-campaigns': ['New campaign', 'plus', { sheet: 'trackCampaign' }, ['utm-campaigns', 'create']],
  'utm-links': ['Create URL', 'plus', { to: '/url-tracking/create' }, ['utm-links', 'create']],
  'utm-analytics': ['Tracking URLs', 'link', { to: '/url-tracking/links' }, ['utm-links', 'view']]
};

/*
 * Where "Plan my day" appears. The needs-action queue is the page it was
 * written for; the two boards get it because that is where somebody stands
 * when they ask the question.
 */
const ASK_VIEWS = ['queue', 'pipeline', 'deals'];

export default function PageHeader({ view, onImport, onExport }) {
  const { risking, riskAt, scoring, scoreClients, scoreDeals, setSheet, can } = useApp();
  const navigate = useNavigate();

  const [title, subtitle, icon, tone] = PAGE_META[view] || PAGE_META.dashboard;
  const isCrm = CRM_VIEWS.includes(view);
  const isSettings = view === 'settings';
  const marketing = MARKETING_ACTIONS[view] || null;
  /* The client Import/Export pair acts on the client book, which none of the
     URL Tracking screens are looking at — offering it here would export
     something other than what is on screen. */
  const isTracking = TRACK_VIEWS.includes(view);
  const tracking = TRACK_ACTIONS[view] || null;

  /* `tone` is the page's own accent from PAGE_META; the header card reads it
     off the DOM so each screen tints itself without a rule per page here. */

  return (
    <div className="phead" data-tone={tone}>
      <div>
        <div className="ptitle">
          <div className={`ic ${tone}`} style={{ width: 44, height: 44, borderRadius: 13 }}>
            <Icon name={icon} solid size={22} />
          </div>
          <h2>{title}</h2>
        </div>
        <div className="psub">{subtitle}</div>
      </div>

      <div className="pacts">
        {/*
          * The one that decides what the morning is for.
          *
          * The risk scan beside it reads the client book; this reads the open
          * board — every lead and deal nobody has closed — and puts it in
          * order. It sits on the three screens where somebody is deciding
          * what to do next rather than looking something up, and nowhere
          * else: a button that reorders your day does not belong on a page
          * about one campaign.
          */}
        {ASK_VIEWS.includes(view) ? (
          <AskAI module="queue" label="Plan my day" />
        ) : null}

        {!isCrm && !isSettings && RISK_VIEWS.includes(view) ? (
          <button type="button" className="btn" onClick={scoreClients} disabled={risking}
            title={risking ? 'Scoring the whole book — this takes about a minute' : 'Score every client for churn risk'}>
            {risking
              ? (
                <>
                  <Spinner />
                  {/* Counting up beats a bare spinner: the scan is a minute of
                      model time, and silence for that long reads as hung. */}
                  {riskAt && riskAt.total
                    ? `Scoring ${riskAt.done}/${riskAt.total}…`
                    : 'Scoring…'}
                </>
              )
              : <><Icon name="zap" />AI risk scan</>}
          </button>
        ) : null}

        {/* The dashboard is a summary, not a list: there is no selection to
            export and nothing on the page an import would land in, so the pair
            is left to the screens that actually hold rows. The four list pages
            carry their own pair on the filter bar, beside the controls that
            decide which rows an export would contain. */}
        {!isSettings && !marketing && !isTracking && view !== 'dashboard' && !LIST_VIEWS.includes(view)
          && view !== 'pipeline' && view !== 'deals' && view !== 'demo-bookings' && view !== 'follow-ups'
          && view !== 'demo-reports' && view !== 'sales' && view !== 'sales-reports'
          && view !== 'activities' ? (
          <>
            <button type="button" className="btn" onClick={onImport}><Icon name="up" />Import</button>
            <button type="button" className="btn" onClick={onExport}><Icon name="down" />Export</button>
          </>
        ) : null}

        {isSettings ? null
          : isTracking ? (
            tracking && can(tracking[3][0], tracking[3][1]) ? (
              <button
                type="button"
                className="btn primary"
                onClick={() => (tracking[2].sheet
                  ? setSheet({ kind: tracking[2].sheet })
                  : navigate(tracking[2].to))}
              >
                <Icon name={tracking[1]} />{tracking[0]}
              </button>
            ) : null
          )
            : marketing ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => setSheet(
                marketing[2] ? { kind: marketing[0], preset: marketing[2] } : { kind: marketing[0] }
              )}
            >
              <Icon name="plus" />{marketing[1]}
            </button>
          )
            /* Not on the overview: it is a summary of two boards, and an
               action there would have to pick one of them for you. Both
               boards carry these themselves, beside the rows they act on. */
            : ['pipeline', 'deals'].includes(view) ? (
            <>
              <button type="button" className="btn" onClick={scoreDeals} disabled={scoring}>
                {scoring ? <><Spinner />Scoring…</> : <><Icon name="zap" />AI score deals</>}
              </button>
              {/* No "Add manual" on the pipeline.
                  Every row there comes from the visitor tracker; a hand-typed
                  lead was a third door onto the same board and in fourteen
                  hundred rows there are none. A deal typed in by hand is a
                  deal, and belongs on the Deals page, where the button is. */}
              {view === 'pipeline' ? null : (
                <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'deal' })}>
                  <Icon name="plus" />New deal
                </button>
              )}
            </>
          )
            : view === 'contacts' ? (
              <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'contact' })}>
                <Icon name="plus" />New contact
              </button>
            )
              : view === 'companies' ? (
                <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'company' })}>
                  <Icon name="plus" />New company
                </button>
              )
                /* Activities carries its own Log activity on the card head,
                   beside the list it adds to. A second copy up here was the
                   same button twice on one screen. */
                : view === 'activities' ? null
                  /* Bookings are Calendly's: the page carries its own refresh
                     and export, and there is nothing to create here. */
                  /* A report has nothing to create either, and each of its
                     blocks exports itself on its own dates — one Export up
                     here could only guess which block was meant. */
                  /* The dashboard is the client report now: a page of
                     figures about the book, with nothing on it that a new
                     account would land in. Creating one belongs on the
                     Clients list, which is where the book actually is. */
                  : view === 'demo-bookings' || view === 'follow-ups' || view === 'demo-reports'
                    || view === 'sales-reports' || view === 'dashboard' ? null
                    : (
                      <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'client' })}>
                        <Icon name="plus" />New client
                      </button>
                    )}
      </div>
    </div>
  );
}
